/* ============================================================
   ExperienceSection — compact dark "6+ Years of Experience"
   section. A smile-arc of 9 tilted dashboard / photo cards
   over a navy backdrop with soft brand glow. Drops into the
   home page right after the hero.
   Cards start packed at centre and fan out cinematically on
   scroll-in-view (IntersectionObserver-driven).
   ============================================================ */

import { useState, useEffect, useRef } from 'react'

const LIME = '#38bdf8'

/* ---------- icons + mini-card visuals ---------- */
function Ico({ name, className = 'h-4 w-4' }) {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }
  const p = {
    star: <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7L12 3Z" fill="currentColor" stroke="none" />,
    calendar: <><rect x="3" y="4.5" width="18" height="16" rx="2" {...s} /><path d="M3 9h18M8 3v3M16 3v3" {...s} /></>,
    chat: <path d="M21 12a8 8 0 0 1-11.7 7.1L4 21l1.9-5.3A8 8 0 1 1 21 12Z" {...s} />,
    bullet: <circle cx="12" cy="12" r="6" fill="currentColor" stroke="none" />,
    shield: <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" {...s} />,
    drone: <><circle cx="6" cy="6" r="2.5" {...s} /><circle cx="18" cy="6" r="2.5" {...s} /><circle cx="6" cy="18" r="2.5" {...s} /><circle cx="18" cy="18" r="2.5" {...s} /><rect x="9" y="9" width="6" height="6" rx="1.5" {...s} /><path d="M8 8L6 6M16 8l2-2M8 16l-2 2M16 16l2 2" {...s} /></>,
    bot: <><rect x="4" y="8" width="16" height="11" rx="3" {...s} /><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="13" r="1" fill="currentColor" stroke="none" /><path d="M12 4v4M9 8h6" {...s} /></>,
    users: <><circle cx="9" cy="8" r="3" {...s} /><path d="M3 21v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" {...s} /><circle cx="17" cy="9" r="2" {...s} /><path d="M22 21v-1a4 4 0 0 0-3-3.87" {...s} /></>,
    server: <><rect x="3" y="4" width="18" height="6" rx="1.5" {...s} /><rect x="3" y="14" width="18" height="6" rx="1.5" {...s} /><path d="M7 7h.01M7 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></>,
    bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" {...s} />,
    spark: <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" {...s} />,
    cpu: <><rect x="6" y="6" width="12" height="12" rx="1.5" {...s} /><rect x="9.5" y="9.5" width="5" height="5" rx="0.5" {...s} /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" {...s} /></>,
    arrowUp: <path d="M12 19V5M5 12l7-7 7 7" {...s} />,
  }
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true">{p[name]}</svg>
}

/* Sprint velocity card — tasks shipped per sprint */
/* ---------------------------------------------------------------------------
   Card bodies. Shared shape, so the fan reads as one set rather than nine
   different layouts: a single icon top-left, an uppercase label, one large
   value, and at most one piece of artwork. Anything that needed 6px type to
   fit has been cut.
   --------------------------------------------------------------------------- */

/** Label above a value. The only text pattern the cards use. */
function Stat({ label, value, unit, dark = false }) {
  return (
    <>
      <div className={`text-[7.5px] font-semibold uppercase tracking-[0.12em] ${dark ? 'text-white/60' : 'text-[#6b7280]'}`}>
        {label}
      </div>
      <div className={`mt-0.5 text-[17px] font-extrabold leading-none tracking-tight ${dark ? 'text-white' : 'text-[#0b1226]'}`}>
        {value}
        {unit ? <span className="text-[9px] font-bold"> {unit}</span> : null}
      </div>
    </>
  )
}

function CardIcon({ name, dark = false }) {
  return (
    <span className={`grid size-6 place-items-center rounded-lg ${dark ? 'bg-white/10 text-[#38bdf8]' : 'bg-[#1554f3] text-white'}`}>
      <Ico name={name} className="h-3 w-3" />
    </span>
  )
}

/* Average time to launch. */
function CardChart() {
  return (
    <div className="flex h-full flex-col p-3 text-[#0b1226]">
      <CardIcon name="bolt" />
      <div className="mt-2.5">
        <Stat label="Average build" value="45" unit="days" />
      </div>
      <svg viewBox="0 0 120 40" className="mt-auto h-9 w-full" preserveAspectRatio="none">
        <path d="M0 32 Q15 24 30 26 T60 15 T90 11 T120 6" fill="none" stroke="#1554f3" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M0 32 Q15 24 30 26 T60 15 T90 11 T120 6 V40 H0 Z" fill="url(#exp1)" opacity="0.35" />
        <defs><linearGradient id="exp1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1554f3" stopOpacity="0.45" /><stop offset="1" stopColor="#1554f3" stopOpacity="0" /></linearGradient></defs>
      </svg>
    </div>
  )
}

/* The one card that carries a phrase instead of a figure. */
function CardExpertise() {
  return (
    <div className="flex h-full flex-col justify-between p-3 text-white">
      <CardIcon name="spark" dark />
      <div className="text-[13px] font-extrabold leading-[1.15]">
        Design.<br />
        Build.<br />
        <span className="text-[#38bdf8]">Maintain.</span>
      </div>
    </div>
  )
}

/* Security cover included after launch. */
function CardCyber() {
  return (
    <div className="relative h-full w-full overflow-hidden p-3" style={{ background: 'linear-gradient(180deg,#0b1226 0%,#101a3a 60%,#070b1e 100%)' }}>
      <div aria-hidden className="absolute -top-10 -right-6 h-24 w-24 rounded-full" style={{ background: 'radial-gradient(circle, rgba(21,84,243,0.55), transparent 70%)' }} />
      <div className="relative flex h-full flex-col">
        <CardIcon name="shield" dark />
        <div className="mt-2.5">
          <Stat label="Security cover" value="12" unit="months" dark />
        </div>
        <svg viewBox="0 0 100 22" className="mt-auto h-6 w-full" preserveAspectRatio="none">
          <path d="M0 17 L14 14 L28 16 L42 11 L56 13 L70 7 L84 10 L100 5" fill="none" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M0 17 L14 14 L28 16 L42 11 L56 13 L70 7 L84 10 L100 5 V22 H0 Z" fill="url(#cybergr)" opacity="0.4" />
          <defs><linearGradient id="cybergr" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#38bdf8" stopOpacity="0.7" /><stop offset="1" stopColor="#38bdf8" stopOpacity="0" /></linearGradient></defs>
        </svg>
      </div>
    </div>
  )
}

/* Clients, year on year. The year ticks are the only micro-type kept: they
   carry the growth story the bars are drawn for. */
function CardBars() {
  const years = ['20', '21', '22', '23', '24', '25']
  const heights = [18, 26, 30, 38, 46, 56]
  return (
    <div className="flex h-full flex-col p-3 text-[#0b1226]">
      <CardIcon name="users" />
      <div className="mt-2.5">
        <Stat label="Clients served" value="40+" />
      </div>
      <svg viewBox="0 0 140 60" className="mt-auto h-9 w-full" preserveAspectRatio="none">
        {heights.map((h, i) => (
          <rect key={i} x={8 + i * 22} y={60 - h} width="13" height={h} rx="2.5" fill={i === heights.length - 1 ? '#1554f3' : '#cbd5e1'} />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[6.5px] font-mono text-[#6b7280]">
        {years.map((y, i) => <span key={y} className={i === years.length - 1 ? 'font-bold text-[#1554f3]' : ''}>&apos;{y}</span>)}
      </div>
    </div>
  )
}

/* What ships with every engagement. The three items used to be a list at 7px;
   the count carries it and the page states them in full elsewhere. */
function CardAutomations() {
  return (
    <div className="flex h-full flex-col p-3 text-[#0b1226]">
      <CardIcon name="bot" />
      <div className="mt-2.5">
        <Stat label="Fixed price" value="Always" />
      </div>
      <div className="mt-auto space-y-1.5">
        {['Weekly demos', 'Repo access'].map((t) => (
          <div key={t} className="flex items-center gap-1.5">
            <span className="grid size-3 shrink-0 place-items-center rounded-full bg-[#1554f3] text-white">
              <svg viewBox="0 0 24 24" className="size-2" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
            </span>
            <span className="text-[7.5px] font-semibold">{t}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* Client rating. */
function CardCustomers() {
  return (
    <div className="relative flex h-full w-full flex-col p-3 text-[#0b1226]" style={{ background: 'linear-gradient(180deg,#eef3ff 0%,#ffffff 70%)' }}>
      <CardIcon name="spark" />
      <div className="mt-2.5">
        <Stat label="Client rating" value="4.9" unit="/ 5" />
      </div>
      <div className="mt-1 flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <svg key={i} viewBox="0 0 20 20" className="size-2.5 fill-amber-400" aria-hidden="true">
            <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77 4.8 17.5l.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
          </svg>
        ))}
      </div>
      <div className="mt-auto text-[7.5px] font-semibold text-[#6b7280]">33+ reviews</div>
    </div>
  )
}

/* Countries delivered in. The EU / APAC / AMER split needed 6px type to fit,
   so the headline number carries it alone. */
function CardDrone() {
  return (
    <div className="relative flex h-full w-full flex-col p-3 text-white overflow-hidden" style={{ background: 'linear-gradient(180deg,#0b1226 0%,#1a2347 60%,#0b1226 100%)' }}>
      <svg aria-hidden viewBox="0 0 100 100" className="absolute inset-0 h-full w-full opacity-25">
        <defs>
          <pattern id="dgrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M10 0H0V10" fill="none" stroke="#52b4ff" strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#dgrid)" />
      </svg>
      <div className="relative flex h-full flex-col">
        <CardIcon name="drone" dark />
        <div className="mt-2.5">
          <Stat label="Delivered in" value="10" unit="countries" dark />
        </div>
        <div className="mt-auto flex gap-0.5">
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="h-2.5 flex-1 rounded-sm bg-[#38bdf8]" style={{ opacity: 0.35 + (i / 10) * 0.65 }} />
          ))}
        </div>
      </div>
    </div>
  )
}

/* Reply time. */
function CardUptime() {
  return (
    <div className="relative flex h-full w-full flex-col p-3 text-white overflow-hidden" style={{ background: 'linear-gradient(180deg,#15161a 0%,#0b1226 100%)' }}>
      <CardIcon name="server" dark />
      <div className="mt-2.5">
        <Stat label="We reply within" value="1" unit="day" dark />
      </div>
      <div className="mt-auto grid grid-cols-9 gap-0.5">
        {Array.from({ length: 27 }).map((_, i) => (
          <span key={i} className="h-2 rounded-sm bg-[#38bdf8]" style={{ opacity: 0.3 + (i % 9) / 12 }} />
        ))}
      </div>
    </div>
  )
}

/* On-time delivery. */
function CardLine() {
  return (
    <div className="flex h-full flex-col p-3 text-[#0b1226]">
      <CardIcon name="cpu" />
      <div className="mt-2.5">
        <Stat label="Delivered on time" value="98" unit="%" />
      </div>
      <svg viewBox="0 0 120 40" className="mt-auto h-9 w-full" preserveAspectRatio="none">
        <path d="M0 30 L18 24 L36 27 L54 17 L72 20 L90 10 L120 4" fill="none" stroke="#1554f3" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M0 30 L18 24 L36 27 L54 17 L72 20 L90 10 L120 4 V40 H0 Z" fill="url(#exp2)" opacity="0.3" />
        <defs><linearGradient id="exp2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1554f3" stopOpacity="0.45" /><stop offset="1" stopColor="#1554f3" stopOpacity="0" /></linearGradient></defs>
      </svg>
    </div>
  )
}

/* compact arc: smaller sizes than the showcase version, tighter container */
const CARDS = [
  { kind: 'photo1', l: 5, b: 0, rot: -22, w: 5.4, h: 6.6, black: true },
  { kind: 'chart', l: 14, b: 0.5, rot: -16, w: 5.9, h: 6.9 },
  { kind: 'expertise', l: 24, b: 1, rot: -10, w: 6.4, h: 7.1, black: true },
  { kind: 'field', l: 34, b: 1.5, rot: -5, w: 6.4, h: 6.9, black: true },
  { kind: 'bars', l: 45, b: 1.8, rot: 0, w: 6.9, h: 7.1 },
  { kind: 'expense', l: 56, b: 1.5, rot: 5, w: 6.4, h: 6.9 },
  { kind: 'photoIncome', l: 66, b: 1, rot: 10, w: 6.4, h: 6.9 },
  { kind: 'line', l: 76, b: 0.5, rot: 16, w: 6.1, h: 6.7 },
  { kind: 'peek', l: 85, b: 0, rot: 22, w: 5.1, h: 6.4, black: true },
]

function Card({ c, idx, spread, total }) {
  /* exp-dark marks the dark-surfaced cards. They live inside a light
     section, where the home-light sheet rewrites text-white to dark slate
     and renders their contents invisible; index.css restores them. */
  const inner = c.black ? 'exp-dark bg-[#15161a] ring-1 ring-black/40 text-white' : 'bg-white ring-1 ring-black/[0.04] overflow-hidden'
  const child = (() => {
    switch (c.kind) {
      case 'photo1': return <CardDrone />
      case 'chart': return <CardChart />
      case 'expertise': return <CardExpertise />
      case 'field': return <CardCyber />
      case 'bars': return <CardBars />
      case 'expense': return <CardAutomations />
      case 'photoIncome': return <CardCustomers />
      case 'line': return <CardLine />
      case 'peek': return <CardUptime />
      default: return null
    }
  })()

  /* Packed state: all cards stacked dead-center, tiny rotation tilt for a
     "deck" feel, scaled down and invisible. Spread state: their real arc
     positions. Centre cards fly out first; edge cards last for the fan effect. */
  const centerDistance = Math.abs(idx - (total - 1) / 2) // 0 at centre → max at edges
  const staggerMs = Math.round((total - 1 - centerDistance) * 90) // centre cards lead

  const packedRotation = (idx - (total - 1) / 2) * 3 // ±12° fan in the packed deck
  const left = spread ? c.l : 50
  const bottom = spread ? c.b : 1
  const rotation = spread ? c.rot : packedRotation
  const scale = spread ? 1 : 0.55
  const opacity = spread ? 1 : 0

  return (
    <span
      className={`absolute ${idx % 2 === 1 ? 'hidden md:block' : 'block'}`}
      style={{
        left: `${left}%`,
        bottom: `${bottom}rem`,
        width: `${c.w}rem`,
        height: `${c.h}rem`,
        transform: `translateX(-${spread ? 0 : 50}%) rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: 'bottom center',
        opacity,
        transition:
          'left 1.1s cubic-bezier(0.22, 1, 0.36, 1), ' +
          'bottom 1.1s cubic-bezier(0.22, 1, 0.36, 1), ' +
          'transform 1.2s cubic-bezier(0.34, 1.36, 0.64, 1), ' +
          'opacity 0.7s ease-out',
        transitionDelay: `${staggerMs}ms`,
        willChange: 'transform, opacity',
      }}
    >
      {/* float animation kicks in only once the spread entrance has finished */}
      <span
        className={spread ? 'block h-full w-full animate-float' : 'block h-full w-full'}
        style={{ animationDelay: `${1400 + staggerMs}ms`, animationDuration: '9s' }}
      >
        <span className={`block h-full w-full rounded-[1.1rem] shadow-[0_22px_40px_-18px_rgba(0,0,0,0.55)] ${inner}`}>
          {child}
        </span>
      </span>
    </span>
  )
}

export default function ExperienceSection() {
  /* Cards start packed at centre. IntersectionObserver flips them to
     `spread` the first time the arc actually scrolls into view — so the
     cinematic fan-out plays for the user instead of firing offscreen. */
  const arcRef = useRef(null)
  const [spread, setSpread] = useState(false)

  useEffect(() => {
    const el = arcRef.current
    if (!el || spread) return

    /* Respect reduced-motion preferences: skip the animation, jump
       straight to the spread state so screen readers / motion-sensitive
       users still see the final layout. */
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setSpread(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setSpread(true)
            observer.disconnect()
            break
          }
        }
      },
      { threshold: 0.25, rootMargin: '0px 0px -10% 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [spread])

  return (
    <section className="relative overflow-hidden bg-white" aria-label="Experience" data-revealed={spread ? 'true' : 'false'}>
      <style>{`
        @keyframes exp-star-pop {
          0%   { opacity: 0; transform: scale(0.3) rotate(-25deg); }
          60%  { opacity: 1; transform: scale(1.25) rotate(8deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }
        @keyframes exp-rating-text-in {
          from { opacity: 0; transform: translateY(8px); filter: blur(4px); letter-spacing: 0.04em; }
          to   { opacity: 1; transform: translateY(0); filter: blur(0); letter-spacing: 0; }
        }
        @keyframes exp-star-twinkle {
          0%, 100% { filter: drop-shadow(0 0 0 rgba(250,204,21,0)); }
          50%      { filter: drop-shadow(0 0 6px rgba(250,204,21,0.65)); }
        }
        /* Pre-reveal state — keep elements hidden so the animation has somewhere to come from */
        section[data-revealed="false"] .exp-star,
        section[data-revealed="false"] .exp-rating-text { opacity: 0; }
        section[data-revealed="true"] .exp-star {
          animation: exp-star-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) var(--d, 0s) both,
                     exp-star-twinkle 2.4s ease-in-out calc(1.1s + var(--d, 0s)) infinite;
        }
        section[data-revealed="true"] .exp-rating-text {
          animation: exp-rating-text-in 0.85s cubic-bezier(0.22, 1, 0.36, 1) 0.65s both;
        }
      `}</style>

      {/* light backdrop + brand glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(80% 50% at 50% 0%, #f1f5f9 0%, #f8fafc 60%, #ffffff 100%)' }} />
        <div className="absolute left-1/2 top-0 h-72 w-[42rem] -translate-x-1/2 rounded-full" style={{ background: `radial-gradient(60% 60% at 50% 0%, ${LIME}33, transparent 70%)` }} />
        {/* faint grid */}
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(rgba(15,23,42,0.6) 1px,transparent 1px),linear-gradient(90deg,rgba(15,23,42,0.6) 1px,transparent 1px)', backgroundSize: '52px 52px', maskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)', WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)' }} />
      </div>

      <div className="relative mx-auto max-w-6xl px-5 pt-10 pb-6 sm:pt-16 sm:pb-16 lg:px-8 lg:pt-20 lg:pb-36">
        {/* eyebrow */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <span className="size-1.5 rounded-full" style={{ backgroundColor: LIME }} />
            Proven track record
          </span>
        </div>

        {/* headline */}
        {/* "6+ Years of Experience" measures ~11x the font size. Pinned at 30px it
            needed 331px, more than a 320-360px phone leaves after padding, so it
            broke in two. Below sm the size follows the viewport and the line is
            held together; sm and up are unchanged. */}
        <h2 className="mx-auto mt-4 max-w-3xl whitespace-nowrap text-center font-display text-[clamp(1.25rem,7.4vw,1.875rem)] sm:text-4xl md:text-[clamp(2.1rem,4.6vw,3.4rem)] font-bold leading-[1.05] tracking-tight text-slate-900">
          6+ Years of Experience
        </h2>

        {/* rating — moved up under the headline. Bumped size + stagger-in animation. */}
        <div className="mt-5 flex flex-col items-center text-slate-900">
          <div className="exp-rating-text text-xl sm:text-2xl md:text-[29px] font-semibold leading-tight text-slate-700 text-center">
            Rated <span className="text-slate-900">4.9/5</span> across{' '}
            <span className="text-slate-900">33+</span> client reviews
          </div>
          <div className="flex items-center gap-2 text-[#facc15] mt-2 sm:mt-2.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="exp-star inline-block"
                style={{ ['--d']: `${i * 0.12}s` }}
              >
                <Ico name="star" className="h-6 w-6 sm:h-8 sm:w-8" />
              </span>
            ))}
          </div>
        </div>

        {/* arc of cards */}
        <div ref={arcRef} className="relative mx-auto mt-2 sm:mt-4 h-[9rem] sm:h-[11rem] w-full max-w-5xl lg:mt-6 lg:h-[12rem]">
          {CARDS.map((c, i) => (
            <Card key={i} c={c} idx={i} spread={spread} total={CARDS.length} />
          ))}
        </div>
      </div>
    </section>
  )
}
